import React, { useEffect, useState } from "react";
import { Play, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY_IMAGES } from "@/lib/lime-data";

const CATS = ["All", "Podcast", "Video", "Photoshoot", "Behind the scenes"];

const spanClass = (span) => {
  if (span === "tall") return "md:row-span-2";
  if (span === "wide") return "md:col-span-2";
  return "";
};

export default function Gallery() {
  const [cat, setCat] = useState("All");
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const filtered =
    cat === "All" ? GALLERY_IMAGES : GALLERY_IMAGES.filter((g) => g.category === cat);

  const lightbox = lightboxIdx !== null ? filtered[lightboxIdx] : null;

  const goPrev = (e) => {
    if (e) e.stopPropagation();
    setLightboxIdx((i) => (i === 0 ? filtered.length - 1 : i - 1));
  };
  const goNext = (e) => {
    if (e) e.stopPropagation();
    setLightboxIdx((i) => (i === filtered.length - 1 ? 0 : i + 1));
  };

  useEffect(() => {
    if (lightboxIdx === null) return;
    const total = filtered.length;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        setLightboxIdx((i) => (i === 0 ? total - 1 : i - 1));
      } else if (e.key === "ArrowRight") {
        setLightboxIdx((i) => (i === total - 1 ? 0 : i + 1));
      } else if (e.key === "Escape") {
        setLightboxIdx(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, filtered.length]);

  return (
    <section id="gallery" className="py-24 sm:py-32 border-t border-[#262626] bg-[#0a0a0a]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="font-mono-overline text-[#E4FF00]">Gallery · Recent work</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3">
              Inside the studio.
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {CATS.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 py-2 text-[11px] font-mono uppercase tracking-widest border transition-colors ${
                  cat === c
                    ? "bg-[#E4FF00] text-black border-[#E4FF00]"
                    : "border-[#262626] text-[#A3A3A3] hover:border-[#404040]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-3" data-testid="gallery-grid">
          {filtered.map((g, idx) => {
            const isVideo = g.type === "video";
            return (
              <button
                key={`${g.src}-${idx}`}
                onClick={() => setLightboxIdx(idx)}
                data-testid={`gallery-tile-${idx}`}
                className={`group relative overflow-hidden border border-[#262626] hover:border-[#E4FF00]/60 transition-colors ${spanClass(g.span)}`}
              >
                <img
                  src={g.src}
                  alt={g.caption}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

                {isVideo && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="relative flex items-center justify-center h-14 w-14 rounded-full bg-[#E4FF00] text-black shadow-[0_0_0_8px_rgba(228,255,0,0.18)] group-hover:scale-110 transition-transform duration-300">
                      <Play className="h-6 w-6 fill-black" strokeWidth={0} />
                    </span>
                  </div>
                )}

                <div className="absolute left-3 bottom-3 right-3 text-left">
                  <div className="font-mono-overline text-[#E4FF00]">{g.category}{isVideo && " · Video"}</div>
                  <div className="font-display font-bold text-sm text-[#f4f4f0] mt-1">{g.caption}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {lightbox && (
        <div
          onClick={() => setLightboxIdx(null)}
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
        >
          {lightbox.type === "video" ? (
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative bg-black border border-[#262626] cursor-default"
              style={
                lightbox.aspect === "9/16"
                  ? { width: "min(92vw, 420px)", aspectRatio: "9 / 16", maxHeight: "90vh" }
                  : { width: "min(92vw, 1100px)", aspectRatio: "16 / 9" }
              }
            >
              <iframe
                src={`https://www.youtube.com/embed/${lightbox.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                title={lightbox.caption}
                className="absolute inset-0 w-full h-full"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
              />
            </div>
          ) : (
            <img
              src={lightbox.src}
              alt={lightbox.caption}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] max-w-[92vw] object-contain border border-[#262626] cursor-default"
            />
          )}

          {filtered.length > 1 && (
            <>
              <button
                onClick={goPrev}
                aria-label="Previous"
                data-testid="gallery-lightbox-prev"
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 h-12 w-12 sm:h-14 sm:w-14 flex items-center justify-center bg-[#141414]/80 border border-[#262626] text-[#f4f4f0] hover:bg-[#E4FF00] hover:text-black hover:border-[#E4FF00] transition-colors"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={goNext}
                aria-label="Next"
                data-testid="gallery-lightbox-next"
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 h-12 w-12 sm:h-14 sm:w-14 flex items-center justify-center bg-[#141414]/80 border border-[#262626] text-[#f4f4f0] hover:bg-[#E4FF00] hover:text-black hover:border-[#E4FF00] transition-colors"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center pointer-events-none">
            <div className="font-mono-overline text-[#E4FF00]">{lightbox.category} · {lightboxIdx + 1}/{filtered.length}</div>
            <div className="font-display font-bold text-[#f4f4f0] mt-1">{lightbox.caption}</div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            data-testid="gallery-lightbox-close"
            className="absolute top-6 right-6 text-[#f4f4f0] text-sm font-mono uppercase tracking-widest hover:text-[#E4FF00] transition-colors"
          >
            Close ✕
          </button>
        </div>
      )}
    </section>
  );
}
